/**
 * Per-agent settings body — model selection, permission mode, and (for OpenCode)
 * provider / base URL / provider-name fields. Supports both the standalone
 * "card" layout and an `embedded` mode used by the unified AgentCard.
 */
import type {
  AgentInfo,
  AgentPermissionMode,
  AgentProviderMode,
  AgentSettingsResponse,
  AgentType,
  OpenCodeProvider,
  SaveAgentSettingsRequest,
} from '@simple-agent-manager/shared';
import {
  AGENT_PERMISSION_MODE_LABELS,
  OPENCODE_PROVIDER_OPTIONS,
  OPENCODE_PROVIDERS,
  PLATFORM_AI_MODELS,
  VALID_PERMISSION_MODES,
} from '@simple-agent-manager/shared';
import { Alert, Card } from '@simple-agent-manager/ui';
import { useEffect, useState } from 'react';

import { ModelSelect } from './ModelSelect';

const DEFAULT_SUCCESS_BANNER_MS = 3000;
const SUCCESS_BANNER_MS = Number(
  import.meta.env.VITE_SUCCESS_BANNER_MS ?? DEFAULT_SUCCESS_BANNER_MS,
);

export interface AgentSettingsCardProps {
  agent: AgentInfo;
  settings: AgentSettingsResponse | null;
  onSave: (agentType: AgentType, data: SaveAgentSettingsRequest) => Promise<void>;
  onReset: (agentType: AgentType) => Promise<void>;
  /**
   * When true, render only the configuration body (no outer border, no agent name
   * header). Used when the card is embedded inside a larger unified AgentCard that
   * already shows the agent name and status.
   */
  embedded?: boolean;
}

/**
 * Per-agent settings card for model selection and permission mode.
 */
export function AgentSettingsCard({
  agent,
  settings,
  onSave,
  onReset,
  embedded = false,
}: AgentSettingsCardProps) {
  const [model, setModel] = useState(settings?.model ?? '');
  const [permissionMode, setPermissionMode] = useState<AgentPermissionMode>(
    settings?.permissionMode ?? 'default'
  );
  const [opencodeProvider, setOpencodeProvider] = useState<OpenCodeProvider | ''>(
    settings?.opencodeProvider ?? ''
  );
  const [opencodeBaseUrl, setOpencodeBaseUrl] = useState(settings?.opencodeBaseUrl ?? '');
  const [opencodeProviderName, setOpencodeProviderName] = useState(settings?.opencodeProviderName ?? '');
  const [providerMode, setProviderMode] = useState<AgentProviderMode | ''>(
    settings?.providerMode ?? ''
  );
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isOpenCode = agent.id === 'opencode';
  const supportsSamProvider = agent.id === 'claude-code' || agent.id === 'openai-codex';
  const supportsOAuthProvider = agent.id === 'claude-code';
  const selectedProvider = opencodeProvider || null;
  const providerMeta = selectedProvider ? OPENCODE_PROVIDERS[selectedProvider] : null;
  const showBaseUrl = selectedProvider === 'custom' || selectedProvider === 'openai-compatible';

  const formControlClass =
    'w-full min-h-11 py-2 px-3 rounded-sm border border-border-default bg-inset text-fg-primary text-sm outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring box-border';

  // Sync state when settings prop changes
  useEffect(() => {
    setModel(settings?.model ?? '');
    setPermissionMode(settings?.permissionMode ?? 'default');
    setOpencodeProvider(settings?.opencodeProvider ?? '');
    setOpencodeBaseUrl(settings?.opencodeBaseUrl ?? '');
    setOpencodeProviderName(settings?.opencodeProviderName ?? '');
    setProviderMode(settings?.providerMode ?? '');
  }, [settings]);

  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(false);
      setSaving(true);

      const data: SaveAgentSettingsRequest = {
        model: model.trim() || null,
        permissionMode,
      };

      if (isOpenCode) {
        data.opencodeProvider = opencodeProvider || null;
        data.opencodeBaseUrl = opencodeBaseUrl.trim() || null;
        data.opencodeProviderName = opencodeProviderName.trim() || null;
      }

      if (supportsSamProvider) {
        data.providerMode = providerMode || null;
      }

      await onSave(agent.id, data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), SUCCESS_BANNER_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setError(null);
      setSuccess(false);
      setResetting(true);
      await onReset(agent.id);
      setModel('');
      setPermissionMode('default');
      setOpencodeProvider('');
      setOpencodeBaseUrl('');
      setOpencodeProviderName('');
      setProviderMode('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), SUCCESS_BANNER_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset settings');
    } finally {
      setResetting(false);
    }
  };

  const modelPlaceholder = (() => {
    if (isOpenCode && providerMeta) {
      return providerMeta.modelPlaceholder;
    }
    switch (agent.id) {
      case 'claude-code':
        return 'e.g. claude-opus-4-6, claude-sonnet-4-5-20250929';
      case 'openai-codex':
        return 'e.g. gpt-5-codex, o3';
      case 'google-gemini':
        return 'e.g. gemini-2.5-pro';
      case 'opencode':
        return 'e.g. scaleway/qwen3-coder-30b-a3b-instruct';
      default:
        return 'Model identifier';
    }
  })();

  const hasChanges = (() => {
    if ((model.trim() || null) !== (settings?.model ?? null)) return true;
    if (permissionMode !== (settings?.permissionMode ?? 'default')) return true;
    if (isOpenCode) {
      if ((opencodeProvider || null) !== (settings?.opencodeProvider ?? null)) return true;
      if ((opencodeBaseUrl.trim() || null) !== (settings?.opencodeBaseUrl ?? null)) return true;
      if ((opencodeProviderName.trim() || null) !== (settings?.opencodeProviderName ?? null)) return true;
    }
    if (supportsSamProvider) {
      if ((providerMode || null) !== (settings?.providerMode ?? null)) return true;
    }
    return false;
  })();

  const body = (
    <>
      {error && (
        <div className="mb-3">
          <Alert variant="error" onDismiss={() => setError(null)}>{error}</Alert>
        </div>
      )}

      {success && (
        <div className="mb-3">
          <Alert variant="success">Settings saved</Alert>
        </div>
      )}

      {/* OpenCode provider selection */}
      {isOpenCode && (
        <div className="mb-4">
          <label htmlFor={`opencode-provider-${agent.id}`} className="text-sm font-medium text-fg-primary mb-1 block">Inference Provider</label>
          <div className="text-xs text-fg-muted mb-2">
            Select the AI provider for OpenCode inference. Leave as &quot;Default&quot; to auto-detect.
          </div>
          <select
            id={`opencode-provider-${agent.id}`}
            value={opencodeProvider}
            onChange={(e) => {
              const val = e.target.value as OpenCodeProvider | '';
              const prev = opencodeProvider;
              setOpencodeProvider(val);
              if (val !== 'custom' && val !== 'openai-compatible') {
                setOpencodeBaseUrl('');
              }
              if (val !== 'custom') {
                setOpencodeProviderName('');
              }
              if ((val === 'platform') !== (prev === 'platform')) {
                setModel('');
              }
            }}
            className={formControlClass}
            data-testid="opencode-provider-select"
          >
            <option value="">Default (auto-detect)</option>
            {OPENCODE_PROVIDER_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {OPENCODE_PROVIDERS[p].label}
              </option>
            ))}
          </select>
          {providerMeta && !providerMeta.requiresApiKey && (
            <div className="text-xs text-fg-muted py-2 px-3 rounded-md bg-inset mt-2 border border-border-default">
              {providerMeta.keyHelpText}
            </div>
          )}
        </div>
      )}

      {isOpenCode && showBaseUrl && (
        <div className="mb-4">
          <label htmlFor={`opencode-base-url-${agent.id}`} className="text-sm font-medium text-fg-primary mb-1 block">Base URL</label>
          <div className="text-xs text-fg-muted mb-2">
            The HTTPS endpoint for your provider&apos;s API.
          </div>
          <input
            id={`opencode-base-url-${agent.id}`}
            type="url"
            value={opencodeBaseUrl}
            onChange={(e) => setOpencodeBaseUrl(e.target.value)}
            placeholder="https://api.example.com/v1"
            className={formControlClass}
            data-testid="opencode-base-url-input"
          />
        </div>
      )}

      {isOpenCode && selectedProvider === 'custom' && (
        <div className="mb-4">
          <label htmlFor={`opencode-provider-name-${agent.id}`} className="text-sm font-medium text-fg-primary mb-1 block">Provider Name</label>
          <div className="text-xs text-fg-muted mb-2">
            A display name for your custom provider.
          </div>
          <input
            id={`opencode-provider-name-${agent.id}`}
            type="text"
            value={opencodeProviderName}
            onChange={(e) => setOpencodeProviderName(e.target.value)}
            placeholder="e.g. My Custom Provider"
            className={formControlClass}
            data-testid="opencode-provider-name-input"
          />
        </div>
      )}

      {/* Provider mode for Claude Code / Codex */}
      {supportsSamProvider && (
        <div className="mb-4">
          <label htmlFor={`provider-mode-${agent.id}`} className="text-sm font-medium text-fg-primary mb-1 block">AI Provider</label>
          <div className="text-xs text-fg-muted mb-2">
            Choose how this agent connects to its AI model. &quot;SAM Platform&quot; uses your SAM AI allowance (no API key needed). &quot;Own API Key&quot; uses your personal key.
            {supportsOAuthProvider ? ' "OAuth Token" uses your subscription token.' : ''}
          </div>
          <select
            id={`provider-mode-${agent.id}`}
            value={providerMode}
            onChange={(e) => setProviderMode(e.target.value as AgentProviderMode | '')}
            className={formControlClass}
            data-testid={`provider-mode-${agent.id}`}
          >
            <option value="">Not configured</option>
            <option value="sam">SAM Platform</option>
            <option value="user-api-key">Own API Key</option>
            {supportsOAuthProvider && <option value="oauth">OAuth Token</option>}
          </select>
          {providerMode === 'sam' && (
            <div className="text-xs text-fg-muted py-2 px-3 rounded-md bg-inset mt-2 border border-border-default">
              AI requests will be routed through the SAM platform proxy. Usage counts against your daily token budget and monthly cost cap. An admin may set allowance ceilings for your account.
            </div>
          )}
        </div>
      )}

      <div className="mb-4">
        <label htmlFor={`model-input-${agent.id}`} className="text-sm font-medium text-fg-primary mb-1 block">Model</label>
        <div className="text-xs text-fg-muted mb-2">
          {isOpenCode && selectedProvider === 'platform'
            ? 'Select a model from the available Workers AI models.'
            : 'Leave empty to use the default model. Model availability depends on your API key or subscription.'}
        </div>
        {isOpenCode && selectedProvider === 'platform' ? (
          <select
            id={`model-input-${agent.id}`}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className={formControlClass}
            data-testid={`model-input-${agent.id}`}
          >
            <option value="">Default ({PLATFORM_AI_MODELS.find((m) => m.isDefault)?.label ?? 'auto'})</option>
            {PLATFORM_AI_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        ) : (
          <ModelSelect
            id={`model-input-${agent.id}`}
            agentType={agent.id}
            value={model}
            onChange={setModel}
            placeholder={modelPlaceholder}
            data-testid={`model-input-${agent.id}`}
          />
        )}
      </div>

      <div className="mb-4" role="group" aria-labelledby={`permission-mode-label-${agent.id}`}>
        <div id={`permission-mode-label-${agent.id}`} className="text-sm font-medium text-fg-primary mb-1">Permission Mode</div>
        <div className="text-xs text-fg-muted mb-2">
          Controls how the agent handles file edits and tool execution.
        </div>
        <div className="flex flex-col gap-2">
          {VALID_PERMISSION_MODES.map((mode) => (
            <label key={mode} className="flex items-center gap-2 text-sm text-fg-primary cursor-pointer">
              <input
                type="radio"
                name={`permission-mode-${agent.id}`}
                value={mode}
                checked={permissionMode === mode}
                onChange={() => setPermissionMode(mode as AgentPermissionMode)}
                data-testid={`permission-mode-${agent.id}-${mode}`}
              />
              {AGENT_PERMISSION_MODE_LABELS[mode] || mode}
            </label>
          ))}
        </div>
        {permissionMode === 'bypassPermissions' && (
          <div role="alert" className="text-xs text-danger-fg py-2 px-3 rounded-md bg-danger-tint mt-1">
            ⚠ Warning: This disables all safety prompts. The agent will execute commands and edit files without confirmation.
          </div>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        <button
          onClick={handleSave}
          disabled={saving || resetting || !hasChanges}
          className={`py-2 px-4 rounded-md border-none bg-accent text-fg-on-accent text-sm font-medium cursor-pointer min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring ${
            saving || resetting || !hasChanges ? 'opacity-50' : 'opacity-100'
          }`}
          data-testid={`save-settings-${agent.id}`}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <button
          onClick={handleReset}
          disabled={saving || resetting}
          className={`py-2 px-4 rounded-md border border-border-default bg-transparent text-fg-muted text-sm font-medium cursor-pointer min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring ${
            saving || resetting ? 'opacity-60' : 'opacity-100'
          }`}
          data-testid={`reset-settings-${agent.id}`}
        >
          {resetting ? 'Resetting...' : 'Reset to Defaults'}
        </button>
      </div>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Card variant="glass" className="p-4" data-testid={`agent-settings-${agent.id}`}>
      <div className="mb-2 font-semibold text-base text-fg-primary">
        {agent.name}
      </div>
      {body}
    </Card>
  );
}
